import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api, switchRole } from '../api';
import { useResource } from './use-resource';
import type { User, Business, MerchantBooking } from '../mock/seeds/schema-types';

// Holds the authenticated merchant (GET /v1/me) and their businesses
// (GET /v1/merchant/businesses), plus the persisted active-business selection (L26).
// Switching the active business goes through PATCH /v1/me/active-business (API-14)
// and bumps `epoch`, which every business-scoped screen includes in its loader deps
// so the whole dashboard re-fetches for the new business. This is the dash-merchant
// analogue of the website's SettingsProvider — one place owns the session shape.
//
// Scope (FR-B4/FR-B23, DR-78 — one rule): a merchant holding more than one business
// starts on "All businesses". The all-scope is a view choice, not a stored selection —
// every list screen (Home, Bookings, Services, Offers, Insights) honours it; the
// single-business screens stay on the persisted active business, which is also where a
// create lands. Picking one business leaves the all-scope and persists it via
// PATCH /me/active-business — the context-bar pill is the only switcher.
//
// Team member (active_role `staff`, API-16; design persona `xh-team`): the business
// list, the active-business switch and the online-pause PATCH are merchant-only, so a
// staff session never calls them. The server scopes a staff token's today / calendar
// reads to the token's business_ids by itself; `GET /me` does not carry those ids
// (auth.yaml#/User has no `business_ids`), so the staff shell names its businesses from
// the calendar rows it may read (API-95 rows carry `business {id, name}`). A staff
// session has no `Business` rows and no active business: `businesses` is empty,
// `activeBusiness` undefined, and every business-scoped owner screen stays inert.
export type BusinessScope = 'all' | 'business';

/** Runs `proceed` now, or after asking (a screen holding unsaved edits, CB-17). */
export type SwitchGuard = (proceed: () => void) => void;

/** A business a team member works at, as named by their calendar rows (API-95). */
export interface TeamBusiness {
  id: string;
  name: string;
  name_bn?: string;
}

interface SessionContextValue {
  user: User | undefined;
  businesses: Business[];
  activeBusiness: Business | undefined;
  loading: boolean;
  error: unknown;
  /** Increments on business switch — a dependency for business-scoped loaders. */
  epoch: number;
  switchBusiness: (businessId: string) => Promise<void>;
  /** 'all' only when the merchant holds more than one business and chose (or kept) it. */
  scope: BusinessScope;
  /** Enter the all-businesses scope (no server write; bumps `epoch`). */
  showAllBusinesses: () => void;
  /** Pause/resume online bookings for the active business (PATCH online_paused). */
  setOnlinePaused: (paused: boolean) => Promise<void>;
  reload: () => void;
  /** A team member's session (active_role `staff`): merchant-only reads are never made. */
  isStaff: boolean;
  /** Signed in, but the account has no business here yet (no merchant or staff role). */
  noMerchantRole: boolean;
  /** Staff only: the businesses their calendar rows name; empty for an owner. */
  teamBusinesses: TeamBusiness[];
  /**
   * A screen holding unsaved edits registers the guard the top switcher runs a business
   * switch through (a switch re-scopes the screen and would drop them). Returns the
   * unregister function. One guard at a time — the latest registration wins.
   */
  registerSwitchGuard: (guard: SwitchGuard) => () => void;
  /** Run a switch (or another leave the switcher offers) through the registered guard, or at once. */
  guardSwitch: (action: () => void) => void;
}

interface SessionData {
  user: User;
  businesses: Business[];
  teamBusinesses: TeamBusiness[];
  /** Signed in, but the account holds no merchant or staff role (no business yet). */
  noMerchantRole?: boolean;
}

// The calendar page a staff session names its businesses from (API-95 max page).
const TEAM_ROWS = 50;

function teamBusinessesOf(rows: readonly MerchantBooking[]): TeamBusiness[] {
  const out = new Map<string, TeamBusiness>();
  for (const r of rows) {
    const id = r.business?.id ?? r.business_id;
    if (!id || out.has(id)) continue;
    out.set(id, { id, name: r.business?.name ?? '', ...(r.business?.name_bn ? { name_bn: r.business.name_bn } : {}) });
  }
  return [...out.values()];
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  children,
  initialScope = 'all',
}: {
  children: ReactNode;
  /** Test hook — the dashboard always starts on 'all'. */
  initialScope?: BusinessScope;
}) {
  const [epoch, setEpoch] = useState(0);
  const [wantAll, setWantAll] = useState(initialScope === 'all');

  const { data, loading, error, reload } = useResource<SessionData>(async (signal) => {
    let user = await api.GET('/me', { signal });
    // One sign-in for every surface (client decision 30 Oct 2026, amending DR-38): the
    // shared session may be in another role — opened from the customer dashboard, or
    // left there by a switch in another tab. This dashboard takes the merchant (or
    // staff) role the account holds by itself (API-13 switch-role), so the owner never
    // signs out and in again. An account with neither role has no business yet.
    if (user.active_role !== 'merchant' && user.active_role !== 'staff') {
      const role = user.roles.includes('merchant') ? 'merchant' : user.roles.includes('staff') ? 'staff' : null;
      if (!role) return { user, businesses: [], teamBusinesses: [], noMerchantRole: true };
      await switchRole(role);
      user = await api.GET('/me', { signal });
    }
    if (user.active_role === 'staff') {
      // Staff-safe (API-16): the calendar in the token's own scope — no business_id.
      const rows = await api
        .GET('/merchant/bookings', { params: { query: { tab: 'all', limit: TEAM_ROWS } }, signal })
        .then((r) => r.items ?? [])
        .catch(() => [] as MerchantBooking[]);
      return { user, businesses: [], teamBusinesses: teamBusinessesOf(rows) };
    }
    const list = await api.GET('/merchant/businesses', { signal });
    return { user, businesses: list.items ?? [], teamBusinesses: [] };
  }, []);

  const isStaff = data?.user.active_role === 'staff';

  const switchBusiness = useCallback(
    async (businessId: string) => {
      if (isStaff) return; // PATCH /me/active-business is merchant-only (API-16)
      await api.PATCH('/me/active-business', { body: { business_id: businessId } });
      setWantAll(false);
      setEpoch((e) => e + 1);
      reload();
    },
    [isStaff, reload],
  );
  const showAllBusinesses = useCallback(() => {
    setWantAll(true);
    setEpoch((e) => e + 1);
  }, []);

  const switchGuard = useRef<SwitchGuard | null>(null);
  const registerSwitchGuard = useCallback((guard: SwitchGuard) => {
    switchGuard.current = guard;
    return () => {
      if (switchGuard.current === guard) switchGuard.current = null;
    };
  }, []);
  const guardSwitch = useCallback((action: () => void) => {
    const guard = switchGuard.current;
    if (guard) guard(action);
    else action();
  }, []);

  const activeBusinessId = data?.businesses.find(
    (b) => b.id === data.user.active_business_id,
  )?.id ?? data?.businesses[0]?.id;

  const setOnlinePaused = useCallback(
    async (paused: boolean) => {
      if (!activeBusinessId) return;
      await api.PATCH('/merchant/businesses/{id}', {
        params: { path: { id: activeBusinessId } },
        body: { online_paused: paused },
      });
      reload();
    },
    [activeBusinessId, reload],
  );

  const value = useMemo<SessionContextValue>(() => {
    const user = data?.user;
    const businesses = data?.businesses ?? [];
    const activeBusiness =
      businesses.find((b) => b.id === user?.active_business_id) ?? businesses[0];
    return {
      user,
      businesses,
      activeBusiness,
      loading,
      error,
      epoch,
      switchBusiness,
      scope: wantAll && businesses.length > 1 ? 'all' : 'business',
      showAllBusinesses,
      setOnlinePaused,
      reload,
      isStaff,
      noMerchantRole: data?.noMerchantRole === true,
      teamBusinesses: data?.teamBusinesses ?? [],
      registerSwitchGuard,
      guardSwitch,
    };
  }, [data, loading, error, epoch, wantAll, switchBusiness, showAllBusinesses, setOnlinePaused, reload, isStaff, registerSwitchGuard, guardSwitch]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within a SessionProvider');
  return ctx;
}
